import { Location, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  model,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  DateRange,
  MatDatepickerInputEvent,
  MatDateRangeInput,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { end } from '@popperjs/core';
import { LegendPosition } from '@swimlane/ngx-charts';
import { filter, finalize } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ModalService } from 'src/app/services/modal-service/modal.service';
import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';

import { Chart, ChartConfiguration, registerables } from 'chart.js';
import {
  formatTitleCaseAndRemoveUnderscore,
  getGraphTitle,
  toTitleCase,
} from 'src/app/shared/utils/graph-helpers';
import { convertToNumber } from 'src/app/shared/utils/convertToNumber';

import ChartDataLabels from 'chartjs-plugin-datalabels';
Chart.register(...registerables);
Chart.register(ChartDataLabels);

@Component({
  selector: 'app-summary-dashboard',
  standalone: false,
  templateUrl: './assessment-report-dashboard.component.html',
  styleUrl: './assessment-report-dashboard.component.scss',
})
export class AssessmentReportDashboardComponent implements OnInit {
  @ViewChild('rangeInput') rangeInput!: MatDateRangeInput<Date>;

  lineChartFilter: string = 'WEEK';
  barChartFilter: string = 'WEEK';
  pieChartFilter: string = 'WEEK';
  filterOpen: boolean = false;

  date: any;

  page: number = 1;
  num: number = 1;
  currentPage: number = 1;
  count: number = 0;
  pageSize: number = 10;
  selectedLimit: number = 10;
  legendPosition = LegendPosition;

  rangePickerForm: FormGroup;
  showSearchForm: boolean;
  searchForm: FormGroup;

  constructor(
    private location: Location,
    private apiService: ApiService,
    private authService: AuthService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private modalService: ModalService
  ) {}

  monthsOfYear = [
    { id: 1, name: 'January' },
    { id: 2, name: 'February' },
    { id: 3, name: 'March' },
    { id: 4, name: 'April' },
    { id: 5, name: 'May' },
    { id: 6, name: 'June' },
    { id: 7, name: 'July' },
    { id: 8, name: 'August' },
    { id: 9, name: 'September' },
    { id: 10, name: 'October' },
    { id: 11, name: 'November' },
    { id: 12, name: 'December' },
  ];

  currentYear = new Date().getFullYear();

  past10Years = Array.from({ length: 10 }, (_, i) => {
    const year = this.currentYear - i;
    return { id: year, name: year.toString() };
  });

  ngOnInit(): void {
    const queryParams = this.route.snapshot.queryParams;
    const { period, startDate, endDate } = queryParams;

    this.searchForm = this.formBuilder.group({
      applicationStatus: [null],
      paymentStatus: [null],
      createdBy: [null],
      months: [null],
      year: [null],
      assessmentType: [null],
      taxOffice: [null],
      officer: [null],
      date: [null],
      assessmentCountCondition: [null],
      amount: [null],
      greaterThan: [null],
      lesserThan: [null],
      greaterThanOrEquals: [null],
      lesserThanOrEquals: [null],
    });

    this.rangePickerForm = this.formBuilder.group({
      startDate: null,
      endDate: null,
    });

    const payload: any = { period: period || 'WEEK' };

    if (period === 'DATE_RANGE' && startDate && endDate) {
      payload.startDate = startDate;
      payload.endDate = endDate;

      this.rangePickerForm.patchValue({
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      });
    }

    this.barChart = new Chart('barChart', this.barChartConfig);
    this.pieChart = new Chart('pieChart', this.pieChartConfig);
    this.lineChart = new Chart('lineChart', this.lineChartConfig);

    this.fetchProfilingTypes();
    this.fetchAssessmentCardData();
    this.fetchApprovalRateData();
    this.fetchBarChartData({ periodStatus: this.barChartFilter });
    this.fetchPieChartData({ periodStatus: this.barChartFilter });
    this.fetchLineChartData({ periodStatus: this.barChartFilter });

    this.fetchFulfillmentRateData();
    this.fetchTopPerformingOfficerData();
    this.fetchAssessmentTable(payload);

    console.log('this.pieChart', this.pieChart);
  }

  onConditionChange() {
    this.searchForm.patchValue({
      amount: null,
      greaterThan: null,
      lesserThan: null,
      greaterThanOrEquals: null,
      lesserThanOrEquals: null,
    });
  }

  lineChart: any;
  public lineChartConfig: ChartConfiguration<'line'> = {
    type: 'line',
    data: {
      labels: [],
      datasets: [],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,

      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
      },
      scales: {
        x: {
          title: {
            font: { size: 14 },
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              // Remove .0 and force integer display
              return Number.isInteger(value) ? value : '';
            },
          },
          title: {
            // display: true,
            // text: 'Amount (₦)',
            font: { size: 14 },
          },
        },
      },
    },
  };

  barChart: any;
  public barChartConfig: ChartConfiguration<'bar'> = {
    type: 'bar',
    data: {
      labels: [], // will be set dynamically
      datasets: [], // will be set dynamically
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,

      plugins: {
        legend: {
          display: true,
          position: 'top',
        },
        title: {
          display: false,
        },
      },
      scales: {
        x: {
          stacked: false,
          title: {
            display: true,
            font: {
              size: 14,
            },
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              // Remove .0 and force integer display
              return Number.isInteger(value) ? value : '';
            },
          },
          title: {
            display: true,
            font: {
              size: 14,
            },
          },
        },
      },
    },
  };

  pieChart: any;
  public pieChartConfig: ChartConfiguration<'pie'> = {
    type: 'pie',
    data: {
      labels: [],
      datasets: [
        {
          data: [300, 50],
          backgroundColor: ['#AAC40A', '#FD2424'],
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,

      plugins: {
        legend: {
          display: true,
          position: 'top',
        },
        title: {
          display: false,
        },
        datalabels: {
          color: '#fff',
          font: {
            weight: 'bold',
            size: 16,
          },
          formatter: (value: number) => value, // 👈 just show the count
        },
      },
    },
    plugins: [ChartDataLabels],
  };

  transformBarChartData(data: any[]): ChartConfiguration<'bar'>['data'] {
    const labels = data.map((item) => item.period);

    const approvedCounts = data.map((item) => item.data.approved.totalCount);
    const paidCounts = data.map((item) => item.data.paid.paidCount);
    const pendingCounts = data.map((item) => item.data.pending.totalCount);

    return {
      labels,
      datasets: [
        {
          label: 'Pending',
          data: pendingCounts,
          backgroundColor: '#F0B615',
        },
        {
          label: 'Approved',
          data: approvedCounts,
          backgroundColor: '#0CA811',
        },
        {
          label: 'Paid',
          data: paidCounts,
          backgroundColor: '#0AC4B9',
        },
      ],
    };
  }

  // Date Range Filter
  maxRangeInDays = 7;

  dateRangeFilter = (date: Date | null): boolean => {
    if (!date) return false;

    const start = this.rangePickerForm?.get('start')?.value;
    if (!start) return true; // No start date yet, allow all dates

    const maxEndDate = new Date(start);
    maxEndDate.setDate(maxEndDate.getDate() + (this.maxRangeInDays - 1));

    return date <= maxEndDate;
  };

  onBarChartFilterDateChange() {
    const start = this.rangePickerForm.value?.startDate;
    const end = this.rangePickerForm.value?.endDate;
    const maxDateRange = 7;

    if (start && end) {
      this.fetchBarChartData({
        periodStatus: 'DATE_RANGE',
        startDate: this.formatDateToYMD(start),
        endDate: this.formatDateToYMD(end),
      });
    } else {
    }
  }

  toggleShowSearch() {
    this.showSearchForm = !this.showSearchForm;
  }

  closeSearchForm() {
    this.showSearchForm = false;
  }

  toggleFilterOpen() {
    this.filterOpen = !this.filterOpen;
  }

  // single: any[] = [
  //   {
  //     "name": "Germany",
  //     "value": 8940000
  //   },
  //   {
  //     "name": "USA",
  //     "value": 5000000
  //   },
  //   {
  //     "name": "France",
  //     "value": 7200000
  //   }
  // ];
  transactionsByStatus: any[] = [
    {
      name: 'Approved Transactions',
      series: [
        { name: 'Mon', value: 62000000 },
        { name: 'Tue', value: 48000000 },
        { name: 'Wed', value: 54000000 },
        { name: 'Thu', value: 20000000 },
        { name: 'Fri', value: 25000000 },
        { name: 'Sat', value: 30000000 },
        { name: 'Sun', value: 41000000 },
      ],
    },
    {
      name: 'Initiations Transactions',
      series: [
        { name: 'Mon', value: 15000000 },
        { name: 'Tue', value: 32000000 },
        { name: 'Wed', value: 44000000 },
        { name: 'Thu', value: 30000000 },
        { name: 'Fri', value: 32000000 },
        { name: 'Sat', value: 27000000 },
        { name: 'Sun', value: 35000000 },
      ],
    },
    {
      name: 'Failed Transactions',
      series: [
        { name: 'Mon', value: 7000000 },
        { name: 'Tue', value: 9000000 },
        { name: 'Wed', value: 11000000 },
        { name: 'Thu', value: 13000000 },
        { name: 'Fri', value: 17000000 },
        { name: 'Sat', value: 20000000 },
        { name: 'Sun', value: 14000000 },
      ],
    },
  ];

  multi: any[] = [
    {
      name: 'Mon',
      series: [
        {
          name: 'Initiated Transactions',
          value: 2800000,
        },
        {
          name: 'Approved Transactions',
          value: 2600002,
        },
      ],
    },

    {
      name: 'Teu',
      series: [
        {
          name: 'Initiated Transactions',
          value: 2800000,
        },
        {
          name: 'Approved Transactions',
          value: 2600002,
        },
      ],
    },

    {
      name: 'Wed',
      series: [
        {
          name: 'Initiated Transactions',
          value: 5800000,
        },
        {
          name: 'Approved Transactions',
          value: 5000002,
        },
      ],
    },

    {
      name: 'Thurs',
      series: [
        {
          name: 'Initiated Transactions',
          value: 5500000,
        },
        {
          name: 'Approved Transactions',
          value: 510002,
        },
      ],
    },

    {
      name: 'Fri',
      series: [
        {
          name: 'Initiated Transactions',
          value: 2800000,
        },
        {
          name: 'Approved Transactions',
          value: 2600002,
        },
      ],
    },
    {
      name: 'Sat',
      series: [
        {
          name: 'Initiated Transactions',
          value: 4200000,
        },
        {
          name: 'Approved Transactions',
          value: 3000002,
        },
      ],
    },

    {
      name: 'Sun',
      series: [
        {
          name: 'Initiated Transactions',
          value: 4200000,
        },
        {
          name: 'Approved Transactions',
          value: 3000002,
        },
      ],
    },
  ];

  convertPeriodToFrequency(label: string): string {
    switch (label?.toUpperCase()) {
      case 'TODAY':
        return 'DAILY';
      case 'WEEK':
        return 'WEEKLY';
      case 'MONTH':
        return 'MONTHLY';
      case 'YEAR':
        return 'YEARLY';
      case 'DATE_RANGE':
        return 'DATE_RANGE';
      default:
        return 'UNKNOWN'; // or throw an error, or return null/empty string
    }
  }

  view: [number, number] = [900, 400];

  // options
  showXAxis: boolean = true;
  showYAxis: boolean = true;
  gradient: boolean = true;
  showLegend: boolean = true;
  showXAxisLabel: boolean = true;
  showYAxisLabel: boolean = true;

  barChartColorScheme: any = {
    domain: ['#25775C', '#0CA811'],
  };

  onSelect(event) {}

  setActiveFilter(payload: any) {
    const period = payload?.period;

    if (period !== 'DATE_RANGE') {
      this.rangePickerForm.reset();
    }

    this.barChartFilter = period;

    const queryParams: any = {
      period,
    };

    if (payload.startDate) {
      queryParams.startDate = payload.startDate;
    } else {
      queryParams.startDate = null;
    }

    if (payload.endDate) {
      queryParams.endDate = payload.endDate;
    } else {
      queryParams.endDate = null;
    }

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
    });

    this.filterOpen = false;

    this.currentPage = 1;
  }

  formatActiveFilterTitle(filter: any) {
    switch (filter?.toUpperCase()) {
      case 'DATE_RANGE': {
        const startDate =
          this.rangePickerForm.value?.startDate?.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });

        const endDate = this.rangePickerForm.value?.endDate?.toLocaleDateString(
          'en-US',
          {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }
        );

        return `${startDate} - ${endDate}`;
      }
      default:
        return 'Date'; // or throw an error, or return null/empty string
    }
  }

  back() {
    this.router.navigate(['/admin/dashboard']);
  }

  searchedtransactionsNotFound = false;
  errorFetchingTransactionHistory = false;
  isSearching: boolean = false;

  formatDateToYMD(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  assessmentCardData: any;
  errorAssessmentCardData: any;
  fetchAssessmentCardData() {
    this.assessmentCardData = null;

    this.apiService
      .post('/assessment-report/cards', {
        periodStatus: 'ALL',
      })
      .subscribe(
        (res: any) => {
          this.assessmentCardData = res;
        },
        (error) => {
          this.errorAssessmentCardData = true;
        }
      );
  }

  approvalRateData: any;
  errorFetchingApprovalRateData: any;
  fetchApprovalRateData() {
    this.assessmentCardData = null;

    this.apiService
      .get('/report/performance/approved-assessments-percentage', {})
      .subscribe(
        (res: any) => {
          this.approvalRateData = res;
        },
        (error) => {
          this.errorFetchingApprovalRateData = true;
        }
      );
  }

  fulfillmentRateData: any;
  errorFetchingFulfillmentRateData: any;
  fetchFulfillmentRateData() {
    this.fulfillmentRateData = null;

    this.apiService
      .get('/report/performance/paid-assessments-percentage', {})
      .subscribe(
        (res: any) => {
          this.fulfillmentRateData = res;
        },
        (error) => {
          this.errorFetchingFulfillmentRateData = true;
        }
      );
  }

  topPerformingOfficerCardData: any = {
    officerName: null,
    totalAssessmentAmount: null,
    totalAssessmentCount: null,
  };
  errorTopPerformingOfficerCardData: any;
  fetchTopPerformingOfficerData() {
    this.apiService
      .get('/report/performance/top-officer-assessments-amount', {})
      .subscribe(
        (res: any) => {
          this.topPerformingOfficerCardData.officerName = res.officerName
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          this.topPerformingOfficerCardData.totalAssessmentAmount =
            res.totalAssessmentAmount;
        },
        (error) => {
          this.errorTopPerformingOfficerCardData = true;
        }
      );

    this.apiService
      .get('/report/performance/top-officer-assessments-count', {})
      .subscribe(
        (res: any) => {
          this.topPerformingOfficerCardData.officerName = res.officerName
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          this.topPerformingOfficerCardData.totalAssessmentCount =
            res.totalAssessmentCount;
        },
        (error) => {
          this.errorTopPerformingOfficerCardData = true;
        }
      );
  }

  setBarChartFilter(payload) {
    this.barChartFilter = payload.periodStatus;
    this.fetchBarChartData(payload);
  }

  setLineChartFilter(payload) {
    this.lineChartFilter = payload.periodStatus;
    this.fetchLineChartData(payload);
  }

  setPieChartFilter(payload) {
    this.pieChartFilter = payload.periodStatus;
    this.fetchPieChartData(payload);
  }

  barChartData: any;
  errorFetchingBarChartData: any;
  fetchBarChartData(payload) {
    this.apiService.post('/assessment-report/bar-chart', payload).subscribe(
      (res: any) => {
        const data = res;

        this.barChartConfig.data = this.transformBarChartData(res);
        this.barChart.update();

        this.barChartData = data;
      },
      (error) => {
        this.errorFetchingBarChartData = true;
      }
    );
  }

  pieChartData: any;
  errorFetchingPieChartData: any;
  fetchPieChartData(payload: any) {
    this.apiService.post('/assessment-report/pie-chart', payload).subscribe({
      next: (res: any) => {
        const paidCount = res.paid?.totalCount || 0;
        const unpaidCount = res.unpaid?.totalCount || 0;

        if (res.unpaid?.totalCount === 0 && res.paid?.totalCount === 0) {
          this.pieChartConfig.data.labels = ['No Data'];
          this.pieChartConfig.data.datasets[0].data = [1]; // Dummy value
          this.pieChartConfig.data.datasets[0].backgroundColor = ['#f0f0f0']; // Light gray
          this.pieChartConfig.options.plugins['datalabels'] = {
            display: false,
          };
        } else {
          this.pieChartConfig.data = {
            labels: ['Paid', 'Unpaid'],
            datasets: [
              {
                label: 'Assessments',
                data: [paidCount, unpaidCount],
                backgroundColor: ['#AAC40A', '#FD2424'],
                borderWidth: 1,
              },
            ],
          };

          this.pieChartConfig.options.plugins['datalabels'] = {
            display: true,
            formatter: (value: number) => value,
            color: '#000',
            font: {
              weight: 'bold',
              size: 14,
            },
          };
        }

        this.pieChart.update();
      },
      error: () => {
        this.errorFetchingPieChartData = true;
      },
    });
  }

  lineChartData: any;
  errorFetchingLineChartData: any;
  fetchLineChartData(payload: any) {
    this.errorFetchingLineChartData = false;

    this.apiService.post('/assessment-report/line-chart', payload).subscribe({
      next: (res: any) => {
        console.log(res)
        this.lineChartConfig.data = this.transformLineChartData(res);
        this.lineChart.update();
      },
      error: () => {
        this.errorFetchingLineChartData = true;
      },
    });
  }

  // transformLineChartData(data: any[]): ChartConfiguration<'line'>['data'] {
  //   const labels = data
  //     .filter((item) => item?.type)
  //     .map((item) =>
  //       item.type
  //         .toLowerCase()
  //         .split('_')
  //         .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  //         .join(' ')
  //     );
  //   const values = data.map((item) => item.data.totalAmount);

  //   return {
  //     labels,
  //     datasets: [
  //       {
  //         data: values,
  //         borderColor: '#3CC3DF',
  //         tension: 0.4,
  //         fill: true,
  //         pointRadius: 4,
  //         borderWidth: 2,
  //       },
  //     ],
  //   };
  // }

  transformLineChartData(data: any[]): ChartConfiguration<'line'>['data'] {
    const labels = data
      .filter((item) => item?.period)
      .map((item) =>
        item.period
          .toString()
          .toLowerCase()
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );

    const values = data.map((item) => item?.data?.totalAmount ?? 0);

    return {
      labels,
      datasets: [
        {
          data: values,
          borderColor: '#3CC3DF',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          borderWidth: 2,
        },
      ],
    };
  }


  // Transactions

  transactions: any[];

  updateLimit() {
    this.currentPage = 1;

    this.page = 0;

    this.callDb();
  }

  onPageChange(event: any) {
    this.currentPage = event;
    this.page = event - 1;

    this.callDb();
  }

  callDb() {
    const barChartFilter = this.barChartFilter;
    const payload: any = { period: this.barChartFilter };
    const rangeFormValues = this.rangePickerForm.value;

    if (
      barChartFilter === 'DATE_RANGE' &&
      rangeFormValues.startDate &&
      rangeFormValues.rangeFormValuesendDate
    ) {
      payload.startDate = rangeFormValues.startDate;
      payload.endDate = rangeFormValues.endDate;
    }

    this.fetchAssessmentTable(payload);
  }

  get appliedFilterCount(): number {
    const formValue = this.searchForm.value;
    const condition = formValue.assessmentCountCondition;

    if (condition) {
      switch (condition) {
        case 'Equals To':
          return formValue.amount ? 1 : 0;
        case 'Greater Than':
          return formValue.greaterThan ? 1 : 0;
        case 'Lesser Than':
          return formValue.lesserThan ? 1 : 0;
        case 'Between':
          let count = 0;
          if (formValue.greaterThanOrEquals) count++;
          if (formValue.lesserThanOrEquals) count++;
          return count;
      }

      return 0; // fallback
    }

    // No condition selected: count all filled fields except the condition field
    return Object.entries(formValue).filter(
      ([key, value]) =>
        key !== 'assessmentCountCondition' && value !== null && value !== ''
    ).length;
  }

  assessmentTypes: any[];
  fetchProfilingTypes() {
    this.apiService
      .get(`/utility/profiling-type`)
      .pipe(finalize(() => {}))
      .subscribe((response: any) => {
        this.assessmentTypes = response.map((item) => ({
          ...item,
          category: formatTitleCaseAndRemoveUnderscore(item.category),
          disabled: false,
        }));
      });
  }

  clearSearchFilters() {
    this.searchForm.reset();
    this.showSearchForm = false;
    this.fetchAssessmentTable();
  }

  fetchAssessmentTable(payload?: any) {
    this.transactions = null;
    this.searchedtransactionsNotFound = false;
    this.errorFetchingTransactionHistory = false;
    this.isSearching = true;
    this.showSearchForm = false;
    this.searchForm.markAsPristine();

    this.num = this.page * this.selectedLimit;

    this.pageSize = this.selectedLimit;

    const formValues = this.searchForm?.value;

    const formData: any = {
      page: Number(this.page),
      limit: Number(this.selectedLimit),
      year: formValues.year || this.currentYear,
      months: formValues.months || this.monthsOfYear.map((item) => item.id),

      ...(formValues.assessmentType && {
        type: formValues.assessmentType,
      }),
      ...(formValues.amount && { amount: convertToNumber(formValues.amount) }),
      ...(formValues.greaterThan && {
        greaterThan: convertToNumber(formValues.greaterThan),
      }),
      ...(formValues.lesserThan && {
        lesserThan: convertToNumber(formValues.lesserThan),
      }),
      ...(formValues.greaterThanOrEquals && {
        greaterThanOrEquals: convertToNumber(formValues.greaterThanOrEquals),
      }),
      ...(formValues.lesserThanOrEquals && {
        lesserThanOrEquals: convertToNumber(formValues.lesserThanOrEquals),
      }),
    };

    this.apiService
      .post(`/assessment-report/report-table`, formData)
      .pipe(
        finalize(() => {
          this.isSearching = false;
        })
      )
      .subscribe(
        (response) => {
          this.transactions = response.data;

          this.count = response.total;

          this.searchedtransactionsNotFound = response?.data?.length <= 0;
        },
        (error) => {
          this.modalService.openErrorModal(error?.message);
          this.errorFetchingTransactionHistory = true;
        }
      );
  }

  dropdownOpen: boolean;

  attemptExport(event: MouseEvent) {
    this.showSearchForm = false;
  }

  isExportingExcel: boolean;
  isExportingPdf: boolean;
  isExportingCsv: boolean;
  errorExportingExcel: boolean;
  errorExportingPdf: boolean;
  errorExportingCsv: boolean;
  exportExcel() {
    this.isExportingExcel = true;

    this.apiService
      .excelPost('/report/performance/download/excel', this.transactions)
      .pipe(
        finalize(() => {
          this.isExportingExcel = false;
        })
      )
      .subscribe(
        (response: Blob) => {
          const blob = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          const url = window.URL.createObjectURL(blob);
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = 'performance_report.xlsx';
          downloadLink.click();
          downloadLink.remove();

          window.URL.revokeObjectURL(url);
        },
        (error) => {
          this.modalService.openErrorModal(
            'Error downloading performance report'
          );
          this.errorExportingExcel = true;
        }
      );
  }

  exportPdf() {
    this.isExportingPdf = true;

    this.apiService
      .excelPost('/report/performance/download/pdf', this.transactions)
      .pipe(
        finalize(() => {
          this.isExportingPdf = false;
        })
      )
      .subscribe(
        (response: Blob) => {
          const blob = new Blob([response], { type: 'application/pdf' });
          const blobUrl = window.URL.createObjectURL(blob);
          const file = new Blob([response], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
          const downloadLink = document.createElement('a');
          downloadLink.href = blobUrl;
          downloadLink.target = '_blank';
          downloadLink.download = 'performance_report.pdf';
          downloadLink.click();
        },
        (error) => {
          this.modalService.openErrorModal(
            'Error downloading performance report'
          );
          this.errorExportingExcel = true;
        }
      );
  }

  exportCsv() {
    this.isExportingCsv = true;

    this.apiService
      .excelPost('/report/performance/download/csv', this.transactions)
      .pipe(
        finalize(() => {
          this.isExportingCsv = false;
        })
      )
      .subscribe(
        (response: Blob) => {
          const blob = new Blob([response], { type: 'text/csv;charset=utf-8' });

          const url = window.URL.createObjectURL(blob);
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = 'performance_report.csv';
          downloadLink.click();
          downloadLink.remove();

          window.URL.revokeObjectURL(url);
        },
        (error) => {
          this.modalService.openErrorModal(
            'Error downloading performance report'
          );
          this.errorExportingCsv = true;
        }
      );
  }

  getPercentageColor(percentage: number): string {
    switch (true) {
      case percentage > 75:
        return '#43A047'; // Green
      case percentage >= 50:
        return '#F9A825'; // Amber
      default:
        return '#E53935'; // Red
    }
  }
}
